import { PlusInSquareIcon } from "assets/icons";
import cn from "classnames";
import { If } from "components";
import { MediaDirectoryKeys } from "constants/enums";
import { useBoolean } from "hooks";
import { DirectUploadMedia, useDirectUploadToS3 } from "libs/directUpload";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, RichTextEditor } from "ui";

import { useUpsertUsageGuide, useUsageGuideByScope } from "../../apis";
import {
  DeleteUsageGuideFaqItemModal,
  DeleteUsageGuideModal,
  UsageGuideFaqItemModal,
  UsageGuideFaqTable,
  UsageGuideVideoField
} from "../../components";
import { isRichTextEmpty } from "../../helpers";
import { UsageGuideFaqItem } from "../../productUsageGuides.types";
import { UsageGuideEditorProps } from "./UsageGuideEditor.types";

import styles from "./UsageGuideEditor.module.scss";

const UsageGuideEditor = ({ scope, header }: UsageGuideEditorProps) => {
  const { t } = useTranslation();
  const { uploader } = useDirectUploadToS3();

  const { guide, isLoading } = useUsageGuideByScope(scope);

  // Local draft of the two fields that go up in one PUT. FAQ items are not part
  // of it — each of those writes on its own and comes back already applied,
  // which is exactly the split the two cards below have to make obvious.
  const [howToUseText, setHowToUseText] = useState("");
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoId, setVideoId] = useState<string | null | undefined>(undefined);
  const [isTextEdited, setIsTextEdited] = useState(false);

  const upsertUsageGuide = useUpsertUsageGuide({
    onSuccess: () => {
      setIsTextEdited(false);
      setVideoId(undefined);
    }
  });

  const {
    value: isFaqItemModalOpen,
    setTrue: openFaqItemModal,
    setFalse: closeFaqItemModal
  } = useBoolean(false);

  const {
    value: isDeleteGuideModalOpen,
    setTrue: openDeleteGuideModal,
    setFalse: closeDeleteGuideModal
  } = useBoolean(false);

  const [editableFaqItem, setEditableFaqItem] =
    useState<UsageGuideFaqItem | null>(null);
  const [deleteFaqItemId, setDeleteFaqItemId] = useState<string | null>(null);

  // Mirrors the server list so a drag can reorder rows before the PATCHes land.
  const [faqItems, setFaqItems] = useState<UsageGuideFaqItem[]>([]);

  // Seed the draft once the guide arrives, and re-seed when the scope changes
  // (the same editor is reused for the product's own guide after an override).
  useEffect(() => {
    setHowToUseText(guide?.howToUseText ?? "");
    setVideoUrl(guide?.videoUrl ?? null);
    setVideoId(undefined);
    setIsTextEdited(false);
  }, [guide?.id, scope.productId, scope.categoryId]);

  // Kept separate from the draft above: FAQ writes return the whole guide, so
  // this has to follow every refetch, not just a change of guide id.
  useEffect(() => {
    setFaqItems(guide?.faqItems ?? []);
  }, [guide?.faqItems]);

  // `undefined` means the video was never touched, so it is not dirty; `null`
  // (detached) and an id (attached) both are.
  const isDirty = isTextEdited || videoId !== undefined;

  // In-app navigation can't be intercepted here — the app mounts a plain
  // BrowserRouter, and useBlocker needs a data router — so this only covers
  // reloads and closing the tab. The status chip carries the rest.
  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener("beforeunload", warnBeforeUnload);

    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [isDirty]);

  const handleUploadImage = async (file: File): Promise<string | null> => {
    const media = await uploader(file, {
      directoryKey: MediaDirectoryKeys.ProductUsageGuide
    });

    return media?.url ?? null;
  };

  // Compared against the stored value rather than flipped blindly: Quill fires
  // onChange while it initialises, and typing something back to how it was
  // should not leave a Save button lit up for a no-op write.
  const handleTextChange = (nextValue: string) => {
    setHowToUseText(nextValue);
    setIsTextEdited(nextValue !== (guide?.howToUseText ?? ""));
  };

  const handleVideoChange = (media: DirectUploadMedia | null) => {
    setVideoUrl(media?.url ?? null);
    setVideoId(media?.id ?? null);
  };

  const handleSave = () => {
    upsertUsageGuide.mutate({
      ...scope,
      howToUseText: isRichTextEmpty(howToUseText) ? null : howToUseText,
      ...(videoId === undefined ? {} : { videoId })
    });
  };

  const isGuideEmpty =
    isRichTextEmpty(howToUseText) && !videoUrl && faqItems.length === 0;

  return (
    <div className={styles.editor}>
      {header}

      <section className={styles.editor_card}>
        <div className={styles.editor_cardHeader}>
          <div>
            <h4 className={styles.editor_cardTitle}>{t("How to use")}</h4>
            <p className={styles.editor_cardHint}>
              {t("The text and the video are saved together, with Save.")}
            </p>
          </div>

          <div className={styles.editor_cardActions}>
            <span
              className={cn(styles.editor_status, {
                [styles.editor_status__dirty]: isDirty
              })}
            >
              {isDirty
                ? t("Unsaved changes")
                : guide
                  ? t("All changes saved")
                  : t("Not created yet")}
            </span>

            <Button
              loading={upsertUsageGuide.isLoading}
              disabled={isLoading || !isDirty}
              onClick={handleSave}
            >
              {t("Save")}
            </Button>
          </div>
        </div>

        {/* ReactQuill is uncontrolled: it reads `defaultValue` once, at mount.
            Seeding it from the local draft would hand it the empty initial
            state, because the effect above only runs after this render — so
            read the server value directly, and let `key` remount on change. */}
        <RichTextEditor
          key={guide?.id ?? "new"}
          defaultValue={guide?.howToUseText ?? ""}
          onChange={handleTextChange}
          onUploadImage={handleUploadImage}
        />

        <UsageGuideVideoField
          videoUrl={videoUrl}
          onChange={handleVideoChange}
        />

        {/* Empty is allowed but pointless — say so instead of blocking Save. */}
        <If condition={isGuideEmpty}>
          <p className={styles.editor_warning}>
            {t(
              "Nothing filled in yet. A guide with no text, no video and no questions gives the assistant nothing to answer from."
            )}
          </p>
        </If>
      </section>

      <section className={styles.editor_card}>
        <div className={styles.editor_cardHeader}>
          <div>
            <h4 className={styles.editor_cardTitle}>
              {t("Frequently asked questions")}
            </h4>
            <p className={styles.editor_cardHint}>
              {guide
                ? t(
                    "Questions save on their own, the moment you add, edit, reorder or hide one. Save above does not apply here."
                  )
                : t("Save the guide first to start adding questions.")}
            </p>
          </div>

          <Button
            icon={<PlusInSquareIcon />}
            // A question hangs off a guide id, so the guide has to exist first.
            disabled={!guide}
            onClick={openFaqItemModal}
          >
            {t("Add Question")}
          </Button>
        </div>

        <If condition={Boolean(guide)}>
          <UsageGuideFaqTable
            faqItems={faqItems}
            isLoading={isLoading}
            onEdit={(faqItem) => {
              setEditableFaqItem(faqItem);
              openFaqItemModal();
            }}
            onDelete={setDeleteFaqItemId}
            setFaqItems={setFaqItems}
          />
        </If>
      </section>

      {/* Kept away from Save: the two used to sit side by side, one keystroke
          apart, and they do opposite things. */}
      <If condition={Boolean(guide)}>
        <div className={styles.editor_dangerZone}>
          <p className={styles.editor_cardHint}>
            {t("Removes the guide and every question in it.")}
          </p>

          <Button variant="delete" onClick={openDeleteGuideModal}>
            {t("Delete guide")}
          </Button>
        </div>
      </If>

      <UsageGuideFaqItemModal
        isOpen={isFaqItemModalOpen}
        guideId={guide?.id ?? ""}
        editableItem={editableFaqItem}
        onClose={() => {
          closeFaqItemModal();
          setEditableFaqItem(null);
        }}
      />

      <DeleteUsageGuideFaqItemModal
        isOpen={!!deleteFaqItemId}
        id={String(deleteFaqItemId)}
        onClose={() => setDeleteFaqItemId(null)}
      />

      <DeleteUsageGuideModal
        isOpen={isDeleteGuideModalOpen}
        guideId={guide?.id ?? ""}
        categoryId={guide?.categoryId}
        onClose={closeDeleteGuideModal}
      />
    </div>
  );
};

export default UsageGuideEditor;
